import type {AxiosError} from "axios";
import type {FieldErrors} from "react-hook-form";

import {GEOLOCATION_PERMISSION_DENIED_CODE} from "@/src/modules/core/lib/geolocation-platform";

export type RealtimeConnectTrigger = "initial" | "reconnect" | "user_change";

/** Safe, PII-free diagnostics surfaced in GlitchTip tags / `diagnostics` context. */
export type GlitchtipDiagnostics = {
  network_reason?: NetworkReason;
  realtime_failure_phase?: RealtimeFailurePhase;
  realtime_connect_trigger?: RealtimeConnectTrigger;
  route_feature?: string;
  error_kind?: string;
  axios_code?: string;
  config_fetch_attempts?: number;
  reconnect_attempt?: number;
  document_hidden?: 0 | 1;
  visibility_state?: string;
  connection_effective_type?: string;
  validation_field_paths?: string;
  field_count?: number;
  had_draft?: 0 | 1;
  had_files?: 0 | 1;
  geo_error_code?: number;
  navigator_on_line?: 0 | 1;
  sw_active?: 0 | 1;
  config_fetch_duration_ms?: number;
};

export type NetworkReason =
  | "offline"
  | "aborted"
  | "timeout"
  | "cors"
  | "dns"
  | "network"
  | "unknown";

export type RealtimeFailurePhase =
  | "config_fetch"
  | "ws_open"
  | "ws_auth"
  | "reconnect_exhausted";

const MAX_VALIDATION_FIELD_PATHS = 12;

export function resolveNavigatorOnLineTag(): 0 | 1 | undefined {
  if (typeof navigator === "undefined" || typeof navigator.onLine !== "boolean") {
    return undefined;
  }
  return navigator.onLine ? 1 : 0;
}

export function resolveAxiosCodeTag(axiosError?: AxiosError): string | undefined {
  const code = axiosError?.code?.trim();
  return code || undefined;
}

type NetworkInformationLike = {
  effectiveType?: string;
};

export function collectRealtimeClientDiagnostics(): Pick<
  GlitchtipDiagnostics,
  "navigator_on_line" | "document_hidden" | "visibility_state" | "connection_effective_type"
> {
  const diagnostics: Pick<
    GlitchtipDiagnostics,
    "navigator_on_line" | "document_hidden" | "visibility_state" | "connection_effective_type"
  > = {};

  const onLine = resolveNavigatorOnLineTag();
  if (onLine !== undefined) diagnostics.navigator_on_line = onLine;

  if (typeof document !== "undefined") {
    diagnostics.document_hidden = document.hidden ? 1 : 0;
    const visibility = document.visibilityState?.trim();
    if (visibility) diagnostics.visibility_state = visibility;
  }

  if (typeof navigator !== "undefined") {
    const connection = (navigator as Navigator & {connection?: NetworkInformationLike})
      .connection;
    const effectiveType = connection?.effectiveType?.trim();
    if (effectiveType) diagnostics.connection_effective_type = effectiveType;
  }

  return diagnostics;
}

export function resolveErrorKind(error: unknown): string | undefined {
  if (error == null) return undefined;

  if (error instanceof Error) {
    const name = error.name?.trim();
    const message = error.message?.trim();
    if (name && message) return `${name}: ${truncate(message, 120)}`;
    if (message) return truncate(message, 120);
    if (name) return name;
  }

  if (typeof error === "string" && error.trim()) {
    return truncate(error.trim(), 120);
  }

  return undefined;
}

export function resolveServiceWorkerActiveTag(): 0 | 1 | undefined {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return undefined;
  }
  return navigator.serviceWorker.controller ? 1 : 0;
}

export function resolveNetworkReason(error?: unknown, axiosError?: AxiosError): NetworkReason {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }

  if (axiosError) {
    const code = axiosError.code?.toUpperCase() ?? "";
    const lowerCode = code.toLowerCase();

    if (lowerCode === "err_canceled" || axiosError.message.toLowerCase().includes("canceled")) {
      return "aborted";
    }
    if (lowerCode === "econnaborted" || lowerCode === "etimedout") return "timeout";
    if (lowerCode === "err_name_not_resolved") return "dns";
    if (lowerCode === "err_network") {
      return typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "network";
    }
  }

  const message = (
    error instanceof Error ? error.message : typeof error === "string" ? error : ""
  ).toLowerCase();

  if (message.includes("timeout")) return "timeout";
  if (message.includes("cors") || message.includes("cross-origin")) return "cors";
  if (message.includes("dns") || message.includes("getaddrinfo")) return "dns";
  if (message.includes("abort") || message.includes("cancel")) return "aborted";
  if (message.includes("offline")) {
    return typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "network";
  }
  if (message.includes("network error") || message.includes("failed to fetch")) {
    return typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "network";
  }

  return "unknown";
}

export function inferRealtimeFailurePhase(error: unknown): RealtimeFailurePhase {
  const message = (
    error instanceof Error ? error.message : typeof error === "string" ? error : ""
  ).toLowerCase();

  if (
    message.includes("broadcasting auth") ||
    message.includes("auth response") ||
    message.includes("auth rejected") ||
    message.includes("missing auth")
  ) {
    return "ws_auth";
  }

  if (
    message.includes("invalid broadcasting config") ||
    message.includes("broadcasting config unavailable") ||
    message.includes("broadcasting config")
  ) {
    return "config_fetch";
  }

  if (message.includes("reconnect")) {
    return "reconnect_exhausted";
  }

  if (
    message.includes("websocket") ||
    message.includes("socket_id") ||
    message.includes("pusher")
  ) {
    return "ws_open";
  }

  return "ws_open";
}

export function collectValidationFieldPaths(
  errors: FieldErrors,
  prefix = "",
  depth = 0,
): string[] {
  if (depth > 4) return [];

  const paths: string[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (!value || typeof value !== "object") continue;

    const path = prefix ? `${prefix}.${key}` : key;

    if ("message" in value && value.message) {
      paths.push(path);
      continue;
    }

    paths.push(...collectValidationFieldPaths(value as FieldErrors, path, depth + 1));
  }

  return paths.slice(0, MAX_VALIDATION_FIELD_PATHS);
}

export function formatValidationFieldPaths(paths: string[]): string | undefined {
  if (paths.length === 0) return undefined;
  return paths.slice(0, MAX_VALIDATION_FIELD_PATHS).join(",");
}

export function resolveGeolocationErrorCodeTag(
  error: GeolocationPositionError | null | undefined,
): number | undefined {
  if (!error) return undefined;
  return error.code;
}

export function isGeolocationPermissionDeniedError(
  error: GeolocationPositionError | null | undefined,
): boolean {
  return error?.code === GEOLOCATION_PERMISSION_DENIED_CODE;
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}
